// Single source of truth for how the admin leads table is filtered, sorted and
// paginated. Shared by three consumers so they can never drift apart:
//   • the URL search params of /admin/leads (shareable/bookmarkable views)
//   • the tRPC `leads.list` / `leads.counts` procedures
//   • the XLSX export route, which must export exactly what the screen shows
//
// Keep this module free of runtime Prisma imports — it is bundled into the
// client. Only `import type` from the generated client is allowed.

import { z } from "zod";
import type { Prisma } from "#/generated/prisma/client";
import {
	DELIVERY_MODE_LABELS,
	DELIVERY_MODES,
	GROUP_TYPE_LABELS,
	GROUP_TYPES,
	UNSPECIFIED_LABEL,
} from "./lead-preferences";
import { LEAD_STATUSES, STATUS_LABELS } from "./lead-status";

/** Sentinel used in the `assignedTo` filter for leads nobody is responsible for yet. */
export const UNASSIGNED = "__unassigned__";

/** Sentinel used in the `editions` filter for leads that declared no edition. */
export const NO_EDITION = "__no_edition__";
export const NO_EDITION_LABEL = "بلا طبعة";

/**
 * Sentinel for the two qualification filters, matching UNASSIGNED's role:
 * "the rep hasn't asked yet". Null is a meaningful answer here — it is how a
 * rep finds the leads still missing a qualification — so it needs to be
 * selectable rather than silently lumped in with everything.
 */
export const UNSPECIFIED = "__unspecified__";

export const LEAD_SORT_FIELDS = [
	"createdAt",
	"fullName",
	"status",
	"wilaya",
	"nextFollowUpAt",
	"lastContactedAt",
] as const;
export type LeadSortField = (typeof LEAD_SORT_FIELDS)[number];

export const SORT_LABELS: Record<LeadSortField, string> = {
	createdAt: "تاريخ الطلب",
	fullName: "الاسم",
	status: "الحالة",
	wilaya: "الولاية",
	nextFollowUpAt: "موعد المتابعة",
	lastContactedAt: "آخر تواصل",
};

export const FOLLOW_UP_FILTERS = [
	"any",
	"overdue",
	"today",
	"upcoming",
	"none",
] as const;
export type FollowUpFilter = (typeof FOLLOW_UP_FILTERS)[number];

/**
 * "today" is the rep's call list for the day: due today, plus any earlier
 * follow-up still pending. A follow-up stops being pending once someone logs
 * a contact on or after its day (see `isFollowUpDue` and the lead mutations),
 * so an old date that was handled never lingers here — and "overdue" is only
 * what was actually missed.
 */
export const FOLLOW_UP_LABELS: Record<FollowUpFilter, string> = {
	any: "كل المواعيد",
	overdue: "متابعة متأخرة",
	today: "متابعة اليوم",
	upcoming: "متابعة قادمة",
	none: "بدون موعد",
};

/**
 * The filter payload. Every field is optional so an empty object means
 * "everything" — that is what the page loads with.
 */
export const leadFiltersSchema = z.object({
	/** Set by the training switcher, not a narrowing filter — excluded from hasActiveFilters/countActiveFilters/describeFilters. */
	trainingId: z.string().optional(),
	/** Free text across name, phone and email. */
	search: z.string().trim().max(120).optional(),
	statuses: z.array(z.enum(LEAD_STATUSES)).optional(),
	/** Delivery modes, or UNSPECIFIED for leads not qualified yet. */
	deliveryModes: z.array(z.enum([...DELIVERY_MODES, UNSPECIFIED])).optional(),
	/** Group types, or UNSPECIFIED for leads not qualified yet. */
	groupTypes: z.array(z.enum([...GROUP_TYPES, UNSPECIFIED])).optional(),
	wilayas: z.array(z.string().max(60)).optional(),
	jobStatuses: z.array(z.string().max(60)).optional(),
	sources: z.array(z.string().max(60)).optional(),
	/** User ids (leads any of them is responsible for), or UNASSIGNED for leads nobody is. */
	assignedTo: z.array(z.string().max(60)).optional(),
	/** Edition numbers (`courseNumber`, as strings), or NO_EDITION. */
	editions: z.array(z.string().max(20)).optional(),
	/** Inclusive yyyy-mm-dd bounds on createdAt, read in Algiers local time. */
	dateFrom: z
		.string()
		.regex(/^\d{4}-\d{2}-\d{2}$/)
		.optional(),
	dateTo: z
		.string()
		.regex(/^\d{4}-\d{2}-\d{2}$/)
		.optional(),
	followUp: z.enum(FOLLOW_UP_FILTERS).optional(),
	/** Only leads that left an email address — the ones an emailing can reach. */
	hasEmail: z.boolean().optional(),
});

export type LeadFilters = z.infer<typeof leadFiltersSchema>;

export const leadSortSchema = z.object({
	sortBy: z.enum(LEAD_SORT_FIELDS).default("createdAt"),
	sortDir: z.enum(["asc", "desc"]).default("desc"),
});

export const leadListInputSchema = leadFiltersSchema.extend({
	sortBy: z.enum(LEAD_SORT_FIELDS).default("createdAt"),
	sortDir: z.enum(["asc", "desc"]).default("desc"),
	page: z.number().int().min(1).default(1),
	pageSize: z.number().int().min(10).max(200).default(25),
});

export type LeadListInput = z.infer<typeof leadListInputSchema>;

// Algeria is UTC+1 year-round (no DST), so a fixed offset is exact here and
// keeps "today" meaning the same thing on the server and in the browser.
const ALGIERS_OFFSET = "+01:00";

function startOfDay(day: string) {
	return new Date(`${day}T00:00:00${ALGIERS_OFFSET}`);
}

function endOfDay(day: string) {
	return new Date(`${day}T23:59:59.999${ALGIERS_OFFSET}`);
}

/**
 * Whether a follow-up has come due — its day is today or earlier, in Algiers.
 * A contact logged while it is due completes it.
 */
export function isFollowUpDue(
	followUpAt: Date | string | null | undefined,
	now: Date = new Date(),
) {
	return (
		followUpAt != null &&
		new Date(followUpAt).getTime() <= endOfDay(algiersToday(now)).getTime()
	);
}

/** The latest instant a due follow-up can hold: the end of today in Algiers. */
export function followUpsDueBy(now: Date = new Date()) {
	return endOfDay(algiersToday(now));
}

/** yyyy-mm-dd for "now" in Algiers, used by the follow-up buckets. */
export function algiersToday(now: Date = new Date()) {
	return new Intl.DateTimeFormat("en-CA", {
		timeZone: "Africa/Algiers",
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
	}).format(now);
}

/**
 * Translates the filter payload into a Prisma `where`. Pass `now` explicitly in
 * tests; production callers let it default.
 */
export function buildLeadWhere(
	filters: LeadFilters,
	now: Date = new Date(),
): Prisma.LeadWhereInput {
	const and: Prisma.LeadWhereInput[] = [];

	if (filters.trainingId) {
		and.push({ trainingId: filters.trainingId });
	}

	if (filters.search) {
		const term = filters.search;
		// A phone typed as "05 51 23" should still match "0551 23 45 67".
		const digits = term.replace(/\D/g, "");
		const or: Prisma.LeadWhereInput[] = [
			{ fullName: { contains: term, mode: "insensitive" } },
			{ email: { contains: term, mode: "insensitive" } },
			{ phone: { contains: term } },
			{ notes: { contains: term, mode: "insensitive" } },
		];
		if (digits.length >= 2) or.push({ phone: { contains: digits } });
		and.push({ OR: or });
	}

	if (filters.statuses?.length) {
		and.push({ status: { in: filters.statuses } });
	}

	// Both qualification filters treat UNSPECIFIED as "column IS NULL", so
	// selecting it alongside real values ORs the two together.
	for (const [field, selected] of [
		["deliveryMode", filters.deliveryModes],
		["groupType", filters.groupTypes],
	] as const) {
		if (!selected?.length) continue;
		const values = selected.filter((v) => v !== UNSPECIFIED);
		const or: Prisma.LeadWhereInput[] = [];
		if (values.length) or.push({ [field]: { in: values } });
		if (selected.includes(UNSPECIFIED)) or.push({ [field]: null });
		and.push({ OR: or });
	}

	if (filters.wilayas?.length) {
		and.push({ wilaya: { in: filters.wilayas } });
	}
	if (filters.jobStatuses?.length) {
		and.push({ jobStatus: { in: filters.jobStatuses } });
	}
	if (filters.sources?.length) {
		and.push({ source: { in: filters.sources } });
	}

	if (filters.assignedTo?.length) {
		const ids = filters.assignedTo.filter((id) => id !== UNASSIGNED);
		const wantsUnassigned = filters.assignedTo.includes(UNASSIGNED);
		const or: Prisma.LeadWhereInput[] = [];
		if (ids.length) or.push({ assignees: { some: { userId: { in: ids } } } });
		if (wantsUnassigned) or.push({ assignees: { none: {} } });
		and.push({ OR: or });
	}

	if (filters.editions?.length) {
		const ids = filters.editions
			.filter((value) => value !== NO_EDITION)
			.map(Number)
			.filter(Number.isInteger);
		const or: Prisma.LeadWhereInput[] = [];
		if (ids.length) or.push({ editionId: { in: ids } });
		if (filters.editions.includes(NO_EDITION)) or.push({ editionId: null });
		if (or.length) and.push({ OR: or });
	}

	if (filters.dateFrom || filters.dateTo) {
		and.push({
			createdAt: {
				...(filters.dateFrom ? { gte: startOfDay(filters.dateFrom) } : {}),
				...(filters.dateTo ? { lte: endOfDay(filters.dateTo) } : {}),
			},
		});
	}

	if (filters.followUp && filters.followUp !== "any") {
		const today = algiersToday(now);
		switch (filters.followUp) {
			case "overdue":
				and.push({ nextFollowUpAt: { not: null, lt: startOfDay(today) } });
				break;
			case "today":
				// Due today or still pending from before — see FOLLOW_UP_LABELS.
				and.push({ nextFollowUpAt: { lte: endOfDay(today) } });
				break;
			case "upcoming":
				and.push({ nextFollowUpAt: { gt: endOfDay(today) } });
				break;
			case "none":
				and.push({ nextFollowUpAt: null });
				break;
		}
	}

	if (filters.hasEmail) {
		and.push({ email: { not: null } });
	}

	return and.length ? { AND: and } : {};
}

export function buildLeadOrderBy(
	sortBy: LeadSortField,
	sortDir: "asc" | "desc",
): Prisma.LeadOrderByWithRelationInput[] {
	// Dates can be null (follow-up, last contact); push those to the end either
	// way so an empty column never hides the rows that do have a value.
	const nulls = "last" as const;
	const primary: Prisma.LeadOrderByWithRelationInput =
		sortBy === "nextFollowUpAt" || sortBy === "lastContactedAt"
			? { [sortBy]: { sort: sortDir, nulls } }
			: sortBy === "wilaya"
				? { wilaya: { sort: sortDir, nulls } }
				: { [sortBy]: sortDir };

	// Stable tiebreaker so pagination never repeats or skips a row.
	return sortBy === "createdAt"
		? [primary, { id: "desc" }]
		: [primary, { createdAt: "desc" }, { id: "desc" }];
}

/** True when anything at all is narrowing the list. */
export function hasActiveFilters(filters: LeadFilters) {
	return Boolean(
		filters.search ||
			filters.statuses?.length ||
			filters.deliveryModes?.length ||
			filters.groupTypes?.length ||
			filters.wilayas?.length ||
			filters.jobStatuses?.length ||
			filters.sources?.length ||
			filters.assignedTo?.length ||
			filters.editions?.length ||
			filters.dateFrom ||
			filters.dateTo ||
			(filters.followUp && filters.followUp !== "any") ||
			filters.hasEmail,
	);
}

export function countActiveFilters(filters: LeadFilters) {
	let n = 0;
	if (filters.search) n++;
	if (filters.statuses?.length) n++;
	if (filters.deliveryModes?.length) n++;
	if (filters.groupTypes?.length) n++;
	if (filters.wilayas?.length) n++;
	if (filters.jobStatuses?.length) n++;
	if (filters.sources?.length) n++;
	if (filters.assignedTo?.length) n++;
	if (filters.editions?.length) n++;
	if (filters.dateFrom || filters.dateTo) n++;
	if (filters.followUp && filters.followUp !== "any") n++;
	if (filters.hasEmail) n++;
	return n;
}

/**
 * Human-readable Arabic summary of the active filters. Shown in the export
 * dialog and written into the exported workbook so a shared file always says
 * which slice of the pipeline it represents.
 */
export function describeFilters(
	filters: LeadFilters,
	userNames: Record<string, string> = {},
	/** courseNumber (as a string) → "الطبعة N". */
	editionNames: Record<string, string> = {},
): string[] {
	const out: string[] = [];
	if (filters.search) out.push(`بحث: «${filters.search}»`);
	if (filters.statuses?.length) {
		out.push(
			`الحالة: ${filters.statuses.map((s) => STATUS_LABELS[s] ?? s).join("، ")}`,
		);
	}
	if (filters.deliveryModes?.length) {
		out.push(
			`الصيغة: ${filters.deliveryModes
				.map((v) =>
					v === UNSPECIFIED
						? UNSPECIFIED_LABEL
						: (DELIVERY_MODE_LABELS[v] ?? v),
				)
				.join("، ")}`,
		);
	}
	if (filters.groupTypes?.length) {
		out.push(
			`نوع المجموعة: ${filters.groupTypes
				.map((v) =>
					v === UNSPECIFIED ? UNSPECIFIED_LABEL : (GROUP_TYPE_LABELS[v] ?? v),
				)
				.join("، ")}`,
		);
	}
	if (filters.wilayas?.length)
		out.push(`الولاية: ${filters.wilayas.join("، ")}`);
	if (filters.jobStatuses?.length)
		out.push(`الوضعية: ${filters.jobStatuses.join("، ")}`);
	if (filters.sources?.length)
		out.push(`المصدر: ${filters.sources.join("، ")}`);
	if (filters.assignedTo?.length) {
		out.push(
			`المسؤول: ${filters.assignedTo
				.map((id) => (id === UNASSIGNED ? "غير مُسند" : (userNames[id] ?? id)))
				.join("، ")}`,
		);
	}
	if (filters.editions?.length) {
		out.push(
			`الطبعة: ${filters.editions
				.map((id) =>
					id === NO_EDITION ? NO_EDITION_LABEL : (editionNames[id] ?? `#${id}`),
				)
				.join("، ")}`,
		);
	}
	if (filters.dateFrom || filters.dateTo) {
		out.push(`التاريخ: ${filters.dateFrom ?? "…"} → ${filters.dateTo ?? "…"}`);
	}
	if (filters.followUp && filters.followUp !== "any") {
		out.push(`المتابعة: ${FOLLOW_UP_LABELS[filters.followUp]}`);
	}
	if (filters.hasEmail) out.push("لديه بريد إلكتروني");
	return out;
}
